import { ApiClient } from '../utils/apiClient';

/**
 * Shared dedupe + short-TTL primitives for list endpoints.
 *
 * The pattern solves two concrete problems we hit across multiple pages:
 *
 *   1. React StrictMode deliberately double-invokes effects in dev, so any
 *      top-level data-loading effect fires twice.
 *   2. Production transitions (e.g. auth-state loading flipping false) can
 *      transiently remount a page component, re-firing the same effect.
 *
 * Both manifest as duplicate HTTP requests. Routing the fetch through a
 * module-level instance of one of these classes coalesces concurrent callers
 * onto a single in-flight promise and then serves subsequent callers from a
 * 60s TTL cache. Consumers call `.clear()` before a user-triggered refresh
 * (or after a mutation that invalidates the list) to bypass the cache.
 */

const DEFAULT_TTL_MS = 60 * 1000;

/** A cache holding responses fetched as the signed-in user. */
export interface UserScopedCache {
  clear(): void;
}

const userScopedCaches = new Set<UserScopedCache>();

/**
 * Enrol a cache in {@link clearUserScopedCaches}. Every `DedupedGet` and
 * `DedupedUrlCache` enrols itself; a service with a hand-rolled cache enrols
 * once, at module load.
 */
export function registerUserScopedCache(cache: UserScopedCache): void {
  userScopedCaches.add(cache);
}

/**
 * Drop every response cached as the signed-in user. Called on logout: the app
 * swaps to the login screen without a page load, so module-level caches
 * outlive the session, and the next person to sign in on the tab would be
 * served the last one's payloads — figures their own permissions withhold —
 * for up to a TTL without a request reaching the server. Also called after an
 * anonymisation, which renames a person in every cached list at once (search,
 * People, Users, timelines), and a former name must not outlive it there.
 */
export function clearUserScopedCaches(): void {
  userScopedCaches.forEach((cache) => cache.clear());
}

/**
 * URL-per-instance variant. Pick this when the endpoint never varies at
 * runtime (no path/query parameters), e.g. `/projects`, `/companies`.
 */
export class DedupedGet<T> {
  private cache: T | null = null;
  private cacheTimestamp = 0;
  private inFlight: Promise<T> | null = null;
  /**
   * Bumped by `clear()`. A request already in flight when the cache is cleared
   * still resolves, and must not write its response back afterwards — after a
   * logout that response belongs to the previous user.
   */
  private generation = 0;
  private readonly ttlMs: number;
  private readonly url: string;

  constructor(url: string, ttlMs: number = DEFAULT_TTL_MS) {
    this.url = url;
    this.ttlMs = ttlMs;
    registerUserScopedCache(this);
  }

  async get(apiClient: ApiClient): Promise<T> {
    if (this.cache !== null && Date.now() - this.cacheTimestamp < this.ttlMs) {
      return this.cache;
    }

    if (this.inFlight) {
      return this.inFlight;
    }

    const generation = this.generation;
    this.inFlight = (async () => {
      try {
        const data = await apiClient.get<T>(this.url);
        if (generation === this.generation) {
          this.cache = data;
          this.cacheTimestamp = Date.now();
        }
        return data;
      } finally {
        if (generation === this.generation) this.inFlight = null;
      }
    })();

    return this.inFlight;
  }

  clear(): void {
    this.generation += 1;
    this.cache = null;
    this.cacheTimestamp = 0;
    this.inFlight = null;
  }
}

interface UrlCacheEntry<T> {
  cache: T | null;
  cacheTimestamp: number;
  inFlight: Promise<T> | null;
}

/**
 * URL-keyed variant. Pick this when a single endpoint is hit with several
 * query-string variants in the same render pass (e.g. the Dashboard's team
 * utilisation call, which runs for both the current and previous period).
 *
 * Each URL gets its own cache + in-flight slot, so different URLs never
 * block or poison each other.
 */
export class DedupedUrlCache<T> {
  private entries = new Map<string, UrlCacheEntry<T>>();
  private readonly ttlMs: number;

  constructor(ttlMs: number = DEFAULT_TTL_MS) {
    this.ttlMs = ttlMs;
    registerUserScopedCache(this);
  }

  private slot(url: string): UrlCacheEntry<T> {
    let entry = this.entries.get(url);
    if (!entry) {
      entry = { cache: null, cacheTimestamp: 0, inFlight: null };
      this.entries.set(url, entry);
    }
    return entry;
  }

  async get(apiClient: ApiClient, url: string): Promise<T> {
    const entry = this.slot(url);

    if (entry.cache !== null && Date.now() - entry.cacheTimestamp < this.ttlMs) {
      return entry.cache;
    }

    if (entry.inFlight) {
      return entry.inFlight;
    }

    entry.inFlight = (async () => {
      try {
        const data = await apiClient.get<T>(url);
        entry.cache = data;
        entry.cacheTimestamp = Date.now();
        return data;
      } finally {
        entry.inFlight = null;
      }
    })();

    return entry.inFlight;
  }

  // A request in flight when this runs writes into an entry no longer in the
  // map, so it cannot repopulate the cache.
  clear(): void {
    this.entries.clear();
  }
}

/**
 * URL-keyed sharing of requests in flight, caching nothing. Pick this for a
 * per-record read whose figures must never be served from memory (an edit
 * elsewhere would not show): two mounts asking at once — StrictMode, a
 * transient remount — share one request, and the next ask after it lands goes
 * to the wire.
 */
export class SharedInFlight<T> {
  private readonly inFlight = new Map<string, Promise<T>>();

  constructor() {
    // A request still in flight at logout must not be joined by the next person to sign in.
    registerUserScopedCache(this);
  }

  clear(): void {
    this.inFlight.clear();
  }

  /** `key` defaults to the URL; callers asking after different changes pass different keys. */
  get(apiClient: ApiClient, url: string, key: string = url): Promise<T> {
    const pending = this.inFlight.get(key);
    if (pending) return pending;
    const request = apiClient.get<T>(url).finally(() => {
      if (this.inFlight.get(key) === request) this.inFlight.delete(key);
    });
    this.inFlight.set(key, request);
    return request;
  }

  /** Stops later callers joining a request that began before a change it would not reflect. */
  forget(key: string): void {
    this.inFlight.delete(key);
  }
}
